import {testIdProps} from '@github-ui/test-id-props'
import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, type IconButtonProps} from '@primer/react'
import {clsx} from 'clsx'
import {Fragment, type ReactElement, type RefObject} from 'react'

import styles from './ItemActionMenu.module.css'

/**
 * An action rendered inside a list item's menu.
 */
export type ItemMenuAction = {
  key: string
  /** Returns the menu content, typically an `ActionList.Item` or `ActionList.LinkItem`. */
  render: () => ReactElement | null | undefined
}

/**
 * Props forwarded to the menu toggle. Deliberately narrow rather than derived from
 * `IconButtonProps`, which is polymorphic and widens `as` when spread.
 */
export type ItemActionMenuToggleProps = {
  icon?: IconButtonProps['icon']
  size?: IconButtonProps['size']
  variant?: IconButtonProps['variant']
  className?: string
  'aria-label'?: string
}

export type ItemActionMenuProps = {
  /** Describes the controls in the menu, e.g. "Issue actions". Used for the toggle's accessible name. */
  label: string
  actions: ItemMenuAction[]
  /** Props forwarded to the toggle IconButton. `icon` defaults to the kebab. */
  toggleProps?: ItemActionMenuToggleProps
  anchorRef?: RefObject<HTMLElement | null>
}

/**
 * Menu of secondary actions for a list item. List items only ever show a collapsed menu, so this
 * deliberately does not use a responsive action bar — there is nothing to overflow, and rendering
 * through `ActionList` keeps arbitrary menu content (links, custom items) working.
 */
export function ItemActionMenu({label, actions, toggleProps, anchorRef}: ItemActionMenuProps) {
  if (actions.length === 0) return null

  const {
    'aria-label': ariaLabel,
    icon = KebabHorizontalIcon,
    variant = 'invisible',
    className,
    ...otherToggleProps
  } = toggleProps ?? {}

  const anchorProps = {
    ...otherToggleProps,
    'aria-label': ariaLabel?.trim() || `More ${label}`,
    icon,
    variant,
    className: clsx(styles.toggle, className),
  }

  return (
    <ActionMenu anchorRef={anchorRef}>
      <ActionMenu.Anchor>
        <IconButton {...testIdProps('overflow-menu-anchor')} {...anchorProps} />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay align="end" data-overflow-menu-overlay>
        <ActionList>
          {actions.map(action => (
            <Fragment key={action.key}>{action.render()}</Fragment>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
